
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller} from "react-hook-form";
import { useDropzone, FileRejection } from "react-dropzone";
import { useTranslation } from "react-i18next";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";

import { LABELS } from "~/resources/Labels";
import { ModalDetailBody } from "../../components/commons/modal/ModalDetailBody";
import { FormScriptTypes } from "~/models/Commons ";
import {
  extractScriptTypeDetails,
  getProviders,
} from "~/Utils/Utils";

import { LoadingWrapper } from "../../components/commons/spiner/SimpleLoaderWrapper";
import { SIMPLDialog } from "../../components/commons/dialog/SimplDialog";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { SIMPLInput } from "../../components/commons/input/SIMPLInput";
import { SIMPLSelectDropdown } from "../../components/commons/input/SIMPLSelectDropdown";
import SIMPLFile from "../../components/commons/input/SIMPLFile";

import { useFormOutputTemplate } from "./UseFormOutPutTemplate";

interface OutputTemplateProps {
  onCloseModal: () => void;
  editedItem: FormScriptTypes | null;
  setConfig: Function;
  title?: string;
  refetch: () => void;
  onSuccess?: (title: string, message: string, response: any) => void;
  onError?: (title: string, message: string) => void;
}

interface SelectFieldProps {
  name: "cloudProviderId" | "scriptTypeId";
  control:any;
  label: string;
  placeholder: string;
  options: { label: string; value: string }[];
  error?: string;
  requiredMessage: string;
  testId: string;
}

const OutputTemplateSelect = ({
  name,
  control,
  label,
  placeholder,
  options,
  error,
  requiredMessage,
  testId,
}: SelectFieldProps) => (
  <Controller
    name={name}
    control={control}
    rules={{ required: requiredMessage }}
    render={({ field }) => (
      <SIMPLSelectDropdown
        id={name}
        label={label}
        options={options}
        value={field.value || ""}
        onChange={field.onChange}
        errorLabel={error}
        placeholder={placeholder}
        testId={testId}
        background="white"
        hideErrorLabel={false}
        isRequired
      />
    )}
  />
);

const AddOutputTemplate = ({
  onCloseModal,
  editedItem,
  setConfig,
  refetch,
  title,
  onSuccess,
  onError,
}: OutputTemplateProps) => {
  const {
    handleSubmit,
    control,
    errors,
    submitForm,
    scriptTypeData,
    provider,
  } = useFormOutputTemplate(
    onCloseModal,
    refetch,
    onSuccess,
    onError
  );

  const [outputTemplateOptions, setOutputTemplateOptions] = useState<any[]>(
    []
  );

  const [cloudProviderList, setCloudProviderList] = useState<any[]>([]);

  const { t } = useTranslation();

  const [fileRejectionError, setFileRejectionError] = useState<string | null>(null);
  const onFileChangeFnRef = useRef<((file: File) => void) | null>(null);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
  } = useDropzone({
    maxFiles: 1,
    accept: {
      "text/html": [".html"],
    },
    onDropRejected: (rejections: FileRejection[]) => {
      if (rejections.length > 0) {
        setFileRejectionError(t("OutputTemplateInvalidFileType"));
      }
    },
    onDropAccepted: (acceptedFiles: File[]) => {
      setFileRejectionError(null);
      if (acceptedFiles[0] && onFileChangeFnRef.current) {
        onFileChangeFnRef.current(acceptedFiles[0]);
      }
    },
  });

  useEffect(() => {
    if (provider) {
      setCloudProviderList(getProviders(provider));
    }
  }, [provider]);

  useEffect(() => {
   const content:any = scriptTypeData?.payload;

    if (content) {
      setOutputTemplateOptions(
        extractScriptTypeDetails(content?.content)
      );
    }
  }, [scriptTypeData]);

  const cloudProviderOptions = useMemo(
    () =>
      cloudProviderList?.map((providerItem) => ({
        label: providerItem.cloudProviderName,
        value: providerItem.id,
      })),
    [cloudProviderList]
  );

  const scriptTypeOptions = useMemo(
    () =>
      outputTemplateOptions.map((template) => ({
        label: template.name,
        value: template.id,
      })),
    [outputTemplateOptions]
  );

  const handleOutputTemplateSubmit = (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    handleSubmit(submitForm)(event);

    setConfig({
      key: "",
      direction: LABELS.ASC,
    });
  };

  const renderActions = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label={t("Cancel")}
          secondary
          buttonHandlerAction={onCloseModal}
          testId="output-template-button-cancel"
        />
      </div>

      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized
          label={t(editedItem ? "Update" : "Submit")}
          primary
          testId="output-template-button-submit"
        />
      </div>
    </>
  );

  return (
    <SIMPLDialog
      onCloseModal={onCloseModal}
      title={title ?? t("AddOutputTemplate")}
      actions={renderActions()}
      onSubmit={handleOutputTemplateSubmit}
      showLine
    >
      <LoadingWrapper
        loadingTitle={t("LoadingOutputTemplates")}
        isLoading={false}
        isError={false}
        testId="loading-output-template"
      >
        <ModalDetailBody>
            <div className="flex flex-col w-full space-y-4">
              <SIMPLInput
                name="name"
                control={control}
                rules={{ required: { value: true } }}
                messageRequired={t("NameRequired")}
                label={t("Name")}
                placeholder={t("Name")}
                required
                id="output-template-name"
                testId="output-template-name"
                fontWeight="bold"
                errorLabel={errors.name?.message}
              />

              <OutputTemplateSelect
                name="cloudProviderId"
                control={control}
                label={t("CloudProviders")}
                placeholder={t("CloudProviders")}
                options={cloudProviderOptions}
                error={errors.cloudProviderId?.message as string}
                requiredMessage={t("MessageCloudProviderRequired")}
                testId="output-template-cloud-provider"
              />

              <OutputTemplateSelect
                name="scriptTypeId"
                control={control}
                label={t("ScriptType")}
                placeholder={t("ScriptType")}
                options={scriptTypeOptions}
                error={errors.scriptTypeId?.message as string}
                requiredMessage={t("MessageScriptTypeRequired")}
                testId="output-template-script-type"
              />

              <Controller
                name="file"
                control={control}
                rules={{ required: t("TemplateFileRequired") }}
                render={({ field }) => {
                  onFileChangeFnRef.current = field.onChange;
                  return (
                  <SIMPLFile
                    errors={errors}
                    errorFieldName="file"
                    dragDropFileHereLabel={t(
                      "DragDropFileHereOrClickHere"
                    )}
                    chooseFileLabel={t("ChooseFile")}
                    currentScript={
                      field.value instanceof File
                        ? field.value
                        : null
                    }
                    onFileChange={field.onChange}
                    getRootProps={getRootProps}
                    getInputProps={getInputProps}
                    isDragActive={isDragActive}
                    LABELS={LABELS}
                    labelName={t("TemplateFile")}
                    required
                    testId="output-template-file"
                  />
                  );
                }}
              />

              {fileRejectionError && (
                <div
                  className="flex items-start gap-1 text-error"
                  role="alert"
                  data-test-id="output-template-file-rejection-error"
                >
                  <HighlightOffRoundedIcon
                    fontSize="small"
                    className="mt-[1px] shrink-0"
                  />
                  <span className="text-sm leading-[18px]">
                    {fileRejectionError}
                  </span>
                </div>
              )}
            </div>
        </ModalDetailBody>
      </LoadingWrapper>
    </SIMPLDialog>
  );
};

export default AddOutputTemplate;
